import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, from, map, of, switchMap, tap } from 'rxjs';
import { environment } from 'environments/environment';
import { COOKIE_SESSION_REQUEST } from 'app/core/auth/session.types';

/**
 * Logo de empresa — mismo esquema que UsrFotoService. Fuente, en orden:
 *   1. EMP.LOGO en Oracle  (GET /api/emp-logo/:id — 404 si no tiene)
 *   2. assets/images/logos/<kemp>.{png,jpg,jpeg,webp,svg}  (logos históricos del build)
 *   3. sin logo (null → la vista muestra el placeholder)
 * `version$` cambia al subir un logo, para que las vistas lo recarguen sin caché.
 */
@Injectable({ providedIn: 'root' })
export class EmpLogoService {
    /** Lado mayor máximo: un logo no se recorta (suele ser apaisado), solo se achica. */
    private static readonly LADO_MAX = 512;

    private readonly base = `${environment.urlApi.replace(/\/$/, '')}/api/emp-logo`;
    private readonly version = new BehaviorSubject<number>(Date.now());
    readonly version$ = this.version.asObservable();

    constructor(private http: HttpClient) { }

    url(kemp: number): string {
        return `${this.base}/${kemp}?v=${this.version.value}`;
    }

    /** Extensiones posibles de los logos históricos en assets, en orden de prueba. */
    private static readonly EXTS = ['png', 'jpg', 'jpeg', 'webp', 'svg'];

    /** Siguiente fuente tras un error de carga (BD → assets con cada extensión); null = no hay logo. */
    fallback(src: string, kemp: number): string | null {
        if (src.includes('/api/emp-logo/')) return `assets/images/logos/${kemp}.${EmpLogoService.EXTS[0]}`;
        const m = src.match(/assets\/images\/logos\/\d+\.(\w+)$/);
        const i = m ? EmpLogoService.EXTS.indexOf(m[1]) : -1;
        return i >= 0 && i + 1 < EmpLogoService.EXTS.length
            ? `assets/images/logos/${kemp}.${EmpLogoService.EXTS[i + 1]}`
            : null;
    }

    /**
     * ¿El logo es OSCURO (se pierde sobre el menú casi negro)? true si ≥ 30 % de
     * sus píxeles visibles tienen luminancia < 0.3. Calibrado 2026-10-02 con los
     * logos reales: Gestión Pyme 94,5 % y Urbina Mantención 46,6 % (texto negro /
     * azul marino) → sí; Urbina Group y Amazing IA 0 % → no.
     * Se descarga con la sesión (el <img> de otro origen no deja leer píxeles).
     * Ante cualquier error → false (se muestra como siempre, sin placa).
     */
    analizar(src: string): Observable<{ oscuro: boolean; url: string | null }> {
        const ctx = src.includes('/api/emp-logo/')
            ? { withCredentials: true, context: new HttpContext().set(COOKIE_SESSION_REQUEST, true) }
            : {};
        return this.http.get(src, { ...ctx, responseType: 'blob' }).pipe(
            switchMap(blob => from(this._analizarBlob(blob))),
            catchError(() => of({ oscuro: false, url: null })),
        );
    }

    /**
     * Recorta los márgenes transparentes (la placa quedaba enorme con el logo
     * chico al centro) y mide la proporción oscura sobre lo recortado.
     * `url` = object URL de la versión recortada (null si no hubo nada que recortar).
     */
    private async _analizarBlob(blob: Blob): Promise<{ oscuro: boolean; url: string | null }> {
        const bmp = await createImageBitmap(blob);
        const canvas = document.createElement('canvas');
        canvas.width = bmp.width; canvas.height = bmp.height;
        const c = canvas.getContext('2d', { willReadFrequently: true })!;
        c.drawImage(bmp, 0, 0);
        bmp.close();
        const d = c.getImageData(0, 0, canvas.width, canvas.height).data;

        let visibles = 0, oscuros = 0;
        for (let i = 0; i < d.length; i += 4) {
            const a = d[i + 3] / 255;
            if (a < 0.3) continue;
            const lum = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255;
            visibles += a;
            if (lum < 0.3) oscuros += a;
        }
        const oscuro = visibles ? oscuros / visibles >= 0.3 : false;

        const recortado = EmpLogoService._recortarTransparente(canvas);
        const aclarado = EmpLogoService._aclararOscuros(recortado);
        const url = recortado === canvas && !aclarado ? null
            : await new Promise<string | null>(r => recortado.toBlob(b => r(b ? URL.createObjectURL(b) : null), 'image/png'));
        return { oscuro, url };
    }

    /**
     * Para el menú oscuro: píxeles casi negros (luminancia < 0.3) → gris claro
     * (200-230 según lo oscuros que eran), conservando el alfa (bordes suaves).
     * Los colores del logo (luminancia mayor) quedan intactos. Solo en la vista
     * del menú — la imagen guardada (correo, PDF) no se toca. true si cambió algo.
     */
    private static _aclararOscuros(canvas: HTMLCanvasElement): boolean {
        const c = canvas.getContext('2d', { willReadFrequently: true })!;
        const img = c.getImageData(0, 0, canvas.width, canvas.height);
        const d = img.data;
        let cambios = 0;
        for (let i = 0; i < d.length; i += 4) {
            if (d[i + 3] < 10) continue;
            const lum = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255;
            if (lum >= 0.3) continue;
            const g = Math.round(200 + 30 * (lum / 0.3));
            d[i] = d[i + 1] = d[i + 2] = g;
            cambios++;
        }
        if (cambios) c.putImageData(img, 0, 0);
        return cambios > 0;
    }

    /**
     * Recorta los bordes transparentes (alfa < 20) dejando un margen de 2 % del
     * lado mayor. Devuelve el MISMO canvas si no hay nada que recortar.
     */
    private static _recortarTransparente(src: HTMLCanvasElement): HTMLCanvasElement {
        const w = src.width, h = src.height;
        const d = src.getContext('2d', { willReadFrequently: true })!.getImageData(0, 0, w, h).data;
        let x0 = w, y0 = h, x1 = -1, y1 = -1;
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                if (d[(y * w + x) * 4 + 3] >= 20) {
                    if (x < x0) x0 = x; if (x > x1) x1 = x;
                    if (y < y0) y0 = y; if (y > y1) y1 = y;
                }
            }
        }
        if (x1 < 0) return src; // todo transparente
        const m = Math.round(Math.max(w, h) * 0.02);
        x0 = Math.max(0, x0 - m); y0 = Math.max(0, y0 - m);
        x1 = Math.min(w - 1, x1 + m); y1 = Math.min(h - 1, y1 + m);
        const cw = x1 - x0 + 1, ch = y1 - y0 + 1;
        if (cw >= w && ch >= h) return src;
        const out = document.createElement('canvas');
        out.width = cw; out.height = ch;
        out.getContext('2d')!.drawImage(src, x0, y0, cw, ch, 0, 0, cw, ch);
        return out;
    }

    /** Achica a lado mayor ≤ 512 px (sin recortar) y sube como PNG (conserva transparencia). */
    subir(kemp: number, archivo: File): Observable<void> {
        return from(this._redimensionar(archivo)).pipe(
            switchMap(blob => {
                const form = new FormData();
                form.append('logo', blob, 'logo.png');
                return this.http.put<void>(`${this.base}/${kemp}`, form, {
                    withCredentials: true,
                    context: new HttpContext().set(COOKIE_SESSION_REQUEST, true),
                });
            }),
            tap(() => this.version.next(Date.now())),
        );
    }

    /**
     * Fondo blanco → transparente, para que el logo se vea sobre el menú oscuro.
     * Solo si el borde es mayoritariamente blanco y opaco (logo con fondo dibujado).
     * Quita todo el blanco (también el interior de las letras). Píxeles casi
     * blancos del contorno quedan semitransparentes (antialias), sin halo.
     */
    private static _quitarFondoBlanco(ctx: CanvasRenderingContext2D, w: number, h: number): void {
        const img = ctx.getImageData(0, 0, w, h);
        const d = img.data;
        const BLANCO = 235;      // >= esto en R,G,B = fondo
        const CASI = 200;        // entre CASI y BLANCO = borde suavizado
        const minRGB = (i: number) => Math.min(d[i], d[i + 1], d[i + 2]);
        const esFondo = (i: number) => d[i + 3] > 200 && minRGB(i) >= CASI;

        // Fondo blanco = al menos 60 % del perímetro blanco y opaco (no se exigen
        // las 4 esquinas: hay logos con un elemento que toca una esquina).
        let per = 0, blancos = 0;
        const chk = (p: number) => { per++; const i = p * 4; if (d[i + 3] > 200 && minRGB(i) >= BLANCO) blancos++; };
        for (let x = 0; x < w; x++) { chk(x); chk((h - 1) * w + x); }
        for (let y = 0; y < h; y++) { chk(y * w); chk(y * w + w - 1); }
        if (blancos / per < 0.6) return;

        // TODO el blanco, no solo el que toca el borde: los huecos de las letras
        // (R, B, A…) también son fondo — con relleno desde el borde quedaban blancos.
        const fondo = new Uint8Array(w * h);
        for (let p = 0; p < w * h; p++) {
            const i = p * 4;
            if (!esFondo(i)) continue;
            fondo[p] = 1;
            const m = minRGB(i);
            // blanco puro → 0; casi blanco → alfa proporcional (contorno suave)
            const a = m >= BLANCO ? 0 : Math.round(255 * (BLANCO - m) / (BLANCO - CASI));
            d[i + 3] = Math.min(d[i + 3], a);
        }

        // Contorno: el píxel opaco vecino del fondo es una MEZCLA del color
        // interior con blanco (antialias del original) — sin esto queda un halo
        // claro sobre el menú oscuro. Se le asigna el color interior (el vecino
        // más oscuro a ≤2 px) y una opacidad = cuánto de ese color tenía.
        const orig = new Uint8ClampedArray(d);
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                const p = y * w + x;
                if (fondo[p]) continue;
                let borde = false;
                for (let dy = -1; dy <= 1 && !borde; dy++) {
                    for (let dx = -1; dx <= 1; dx++) {
                        const X = x + dx, Y = y + dy;
                        if (X >= 0 && Y >= 0 && X < w && Y < h && fondo[Y * w + X]) { borde = true; break; }
                    }
                }
                if (!borde) continue;
                let best = -1, bs = Infinity;
                for (let dy = -2; dy <= 2; dy++) {
                    for (let dx = -2; dx <= 2; dx++) {
                        const X = x + dx, Y = y + dy;
                        if (X < 0 || Y < 0 || X >= w || Y >= h || fondo[Y * w + X]) continue;
                        const j = (Y * w + X) * 4;
                        const s = orig[j] + orig[j + 1] + orig[j + 2];
                        if (s < bs) { bs = s; best = j; }
                    }
                }
                const i = p * 4;
                if (best < 0 || best === i) continue;
                let a = 0, n = 0;
                for (let k = 0; k < 3; k++) {
                    const den = 255 - orig[best + k];
                    if (den > 20) { a = Math.max(a, (255 - orig[i + k]) / den); n++; }
                }
                if (!n || a >= 1) continue;
                for (let k = 0; k < 3; k++) d[i + k] = orig[best + k];
                d[i + 3] = Math.round(Math.max(0, a) * orig[i + 3]);
            }
        }
        ctx.putImageData(img, 0, 0);
    }

    private async _redimensionar(archivo: File): Promise<Blob> {
        const bitmap = await createImageBitmap(archivo);
        const escala = Math.min(1, EmpLogoService.LADO_MAX / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * escala);
        canvas.height = Math.round(bitmap.height * escala);
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
        EmpLogoService._quitarFondoBlanco(ctx, canvas.width, canvas.height);
        // Sin márgenes vacíos: el logo llena su caja en el menú y en el correo.
        const final = EmpLogoService._recortarTransparente(canvas);
        return new Promise((resolve, reject) =>
            final.toBlob(b => b ? resolve(b) : reject(new Error('No se pudo procesar la imagen')), 'image/png'));
    }
}
